import { Link } from "react-router";
import { formatInr } from "@/lib/format/inr";
import type { RecurringDetail } from "@/lib/recurring/recurring-detail";
import { CategoryIcon } from "@/components/category-icon/category-icon";
import { formatAdminDateTime, formatCalendarDate } from "@/lib/format/admin-time";
import { formatCalendarAgo } from "@/components/list/format-relative";

export interface RecurringDetailViewProps {
  recurring: RecurringDetail;
  now: Date;
}

// dueDayOfWeek is an ISO weekday: 1 = Monday … 7 = Sunday.
const DAY_OF_WEEK: Record<number, string> = {
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
  7: "Sun",
};

function Chip({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "good" | "warn" | "info" | "bad";
  children: React.ReactNode;
}) {
  const cls = {
    neutral: "text-fc-text-secondary border-fc-border bg-fc-overlay",
    good: "text-fc-brand border-fc-brand/40 bg-fc-brand/10",
    warn: "text-fc-warn border-fc-warn/40 bg-fc-warn/10",
    info: "text-fc-info border-fc-info/40 bg-fc-info/10",
    bad: "text-fc-bad border-fc-bad/40 bg-fc-bad/10",
  }[tone];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${cls}`}
    >
      {children}
    </span>
  );
}

// Moments: admins' clock (IST), labelled.
const toDateTime = formatAdminDateTime;

// startDate / lastPaidDate are calendar dates: shown as stored.
const toDate = formatCalendarDate;

function PropRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="pt-0.5 text-[10.5px] uppercase tracking-wide text-fc-text-dim">
        {label}
      </div>
      <div className="text-[12.5px] text-fc-text">{children}</div>
    </>
  );
}

function cadenceLabel(r: RecurringDetail): string {
  if (r.frequency === "custom" && r.customIntervalValue && r.customIntervalUnit) {
    return `every ${r.customIntervalValue} ${r.customIntervalUnit}`;
  }
  return r.frequency;
}

export function RecurringDetailView({
  recurring,
  now,
}: RecurringDetailViewProps) {
  return (
    <div className="flex flex-col gap-6">
      {/* HERO */}
      <section className="flex items-center gap-4">
        <CategoryIcon emoji={recurring.categoryEmoji} size="xl" />
        <div className="min-w-0">
          <h1 className="text-[24px] font-semibold tracking-tight text-fc-text">
            {recurring.title}
          </h1>
          <div className="text-[13px] text-fc-text-dim">
            {recurring.expectedAmount != null ? (
              <>
                Expected{" "}
                <span className="font-mono font-semibold text-fc-text">
                  {formatInr(recurring.expectedAmount)}
                </span>
                {" "}·{" "}
              </>
            ) : null}
            {cadenceLabel(recurring)}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Chip tone={recurring.isActive ? "good" : "neutral"}>
              {recurring.isActive ? "active" : "inactive"}
            </Chip>
            <Chip tone="info">
              <Link to={`/admin/groups/${recurring.groupId}`}>
                {recurring.groupName}
              </Link>
            </Chip>
            <Chip>{recurring.categoryName}</Chip>
            <Chip tone="warn">{cadenceLabel(recurring)}</Chip>
            {recurring.isFixedAmount ? <Chip>fixed amount</Chip> : null}
            {recurring.paymentMethodName ? (
              <Chip>{recurring.paymentMethodName}</Chip>
            ) : null}
            <span className="font-mono text-[10.5px] tracking-wider text-fc-text-dim">
              r_{recurring.id.slice(0, 8)}
            </span>
          </div>
        </div>
      </section>

      {/* PROP GRID */}
      <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
        <div className="mb-4">
          <div className="text-[13px] font-semibold text-fc-text">
            Recurring properties
          </div>
          <div className="text-[11.5px] text-fc-text-dim">
            Fields from schema
          </div>
        </div>
        <div className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2.5">
          <PropRow label="ID">
            <span className="rounded-md border border-fc-border bg-fc-overlay px-2 py-0.5 font-mono text-[11.5px]">
              {recurring.id}
            </span>
          </PropRow>

          <PropRow label="Status">
            <Chip tone={recurring.isActive ? "good" : "neutral"}>
              {recurring.isActive ? "active" : "inactive"}
            </Chip>
          </PropRow>

          <PropRow label="Group">
            <Link
              to={`/admin/groups/${recurring.groupId}`}
              className="text-fc-text underline decoration-fc-border underline-offset-4 hover:decoration-fc-brand"
            >
              {recurring.groupName}
            </Link>
          </PropRow>

          <PropRow label="Category">
            {recurring.categoryEmoji ? `${recurring.categoryEmoji} ` : ""}
            {recurring.categoryName}
          </PropRow>

          <PropRow label="Creator">
            <Link
              to={`/admin/users/${recurring.creatorId}`}
              className="text-fc-text underline decoration-fc-border underline-offset-4 hover:decoration-fc-brand"
            >
              {recurring.creatorName}
            </Link>
          </PropRow>

          <PropRow label="Responsible">
            {recurring.responsibleUserId && recurring.responsibleUserName ? (
              <Link
                to={`/admin/users/${recurring.responsibleUserId}`}
                className="text-fc-text underline decoration-fc-border underline-offset-4 hover:decoration-fc-brand"
              >
                {recurring.responsibleUserName}
              </Link>
            ) : (
              "—"
            )}
          </PropRow>

          <PropRow label="Payment method">
            {recurring.paymentMethodName ?? "—"}
          </PropRow>

          <PropRow label="Frequency">
            {cadenceLabel(recurring)}
          </PropRow>

          <PropRow label="Fixed amount">
            {recurring.isFixedAmount ? "yes" : "no"}
          </PropRow>

          <PropRow label="Expected amount">
            {recurring.expectedAmount != null ? (
              <span className="font-mono font-semibold">
                {formatInr(recurring.expectedAmount)}
              </span>
            ) : (
              "—"
            )}
          </PropRow>

          <PropRow label="Average amount">
            {recurring.averageAmount != null
              ? formatInr(recurring.averageAmount)
              : "—"}
          </PropRow>

          <PropRow label="Due day">
            {recurring.dueDate != null
              ? `day ${recurring.dueDate} of month`
              : recurring.dueDayOfWeek != null
                ? DAY_OF_WEEK[recurring.dueDayOfWeek] ?? "—"
                : "—"}
          </PropRow>

          <PropRow label="Start date">
            <span className="font-mono text-[12px]">
              {toDate(recurring.startDate)}
            </span>
          </PropRow>

          <PropRow label="Last paid">
            {recurring.lastPaidDate ? (
              <>
                <span className="font-mono text-[12px]">
                  {toDate(recurring.lastPaidDate)}
                </span>
                {recurring.lastPaidAmount != null ? (
                  <>
                    {" "}·{" "}
                    <span className="font-mono">
                      {formatInr(recurring.lastPaidAmount)}
                    </span>
                    <span className="text-fc-text-dim">
                      {" "}·{" "}
                      {formatCalendarAgo(recurring.lastPaidDate, now)}
                    </span>
                  </>
                ) : null}
              </>
            ) : (
              "—"
            )}
          </PropRow>

          <PropRow label="Reminder lead">
            {recurring.reminderLead ?? "—"}
          </PropRow>

          <PropRow label="Occurrences">
            {recurring.expenseCount} expense{recurring.expenseCount === 1 ? "" : "s"} logged
            {" · "}
            {recurring.skipCount} skip{recurring.skipCount === 1 ? "" : "s"}
          </PropRow>

          <PropRow label="Created">
            <span className="font-mono text-[12px]">
              {toDateTime(recurring.createdAt)}
            </span>
          </PropRow>

          <PropRow label="Updated">
            <span className="font-mono text-[12px]">
              {toDateTime(recurring.updatedAt)}
            </span>
          </PropRow>
        </div>
      </section>
    </div>
  );
}
